import React, { useMemo } from 'react';
import { TimeSeriesLineChart } from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { TimePhasedUnitsPoint } from '@/utils/buildTimePhasedUnitsSeries';
import type { EChartsOption } from 'echarts';
import type { EChartProps } from '@/components/visuals/EChart';

export interface UnitsPerDayHistogramChartProps {
  points: TimePhasedUnitsPoint[];
  height?: number | string;
  showDataLabels: boolean;
  density?: ChartDensity;
  /** User clicked a calendar day category (histogram bar). */
  onBarDateClick?: (dateYmd: string) => void;
  /** Highlights the bar for this YYYY-MM-DD day (focus drill-down). */
  highlightedDate?: string | null;
}

/** Time-phased bar chart: planned units per calendar day (project controls histogram). */
export function UnitsPerDayHistogramChart({
  points,
  height = 260,
  showDataLabels = true,
  density = 'overview',
  onBarDateClick,
  highlightedDate = null,
}: UnitsPerDayHistogramChartProps) {
  const xAxisData = useMemo(() => points.map((p) => p.date), [points]);
  const unitsPerDay = useMemo(() => points.map((p) => p.unitsPerDay), [points]);

  const onEvents = useMemo((): NonNullable<EChartProps['onEvents']> | undefined => {
    if (!onBarDateClick) return undefined;
    return {
      click: (raw: unknown) => {
        const p = raw as { name?: string; componentType?: string };
        const name = p?.name;
        if (name && /^\d{4}-\d{2}-\d{2}$/.test(String(name))) {
          onBarDateClick(String(name));
        }
      },
    };
  }, [onBarDateClick]);

  const optionOverrides = useMemo<Partial<EChartsOption>>(
    () => ({
      xAxis: { boundaryGap: true },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const arr = params as Array<{ axisValue?: string; value?: number | string }>;
          const p0 = arr[0];
          if (!p0) return '';
          const d = String(p0.axisValue ?? '');
          const v = typeof p0.value === 'number' ? p0.value.toFixed(2) : String(p0.value ?? '');
          return `${d}<br/>Units/day: ${v}`;
        },
      },
    }),
    []
  );

  return (
    <TimeSeriesLineChart
      xAxisData={xAxisData}
      series={[{ name: 'Planned units/day', data: unitsPerDay, type: 'bar', smooth: false }]}
      height={height}
      showDataLabels={showDataLabels}
      density={density}
      optionOverrides={optionOverrides}
      onEvents={onEvents}
      barHighlightCategory={highlightedDate}
    />
  );
}
